/**
 * SEO tags for one Metal Market Intelligence category page, rendered by
 * `src/pages/metal-resources/metal-market-intelligence/[slug].tsx` beside `./Category.tsx`.
 *
 * Through `components/Shared/model/MetaHead.tsx` it sets the title (`seoTitle`, else `title`),
 * the description, the `og:image` (the hero image) and the canonical path
 * `/metal-resources/metal-market-intelligence/<slug>`, with `company: false` (no JSON-LD).
 *
 * The description falls back to `blurb1.blurb1` when `seoDescription` is empty. `[slug].tsx`
 * delivers `blurb1` as a plain string, so that fallback is `undefined` and the description tags
 * have no content. Every language's entry must have a `heroImage`: the lookup has no optional
 * chaining, so one without throws on render in the browser. The article pages' tags are in
 * `./BlogMetaHead.tsx`.
 */
import React from "react";
import MetaHead, { MetaContent } from "@components/Shared/model/MetaHead";
import { TranslatedBlogCategorySlugs } from "@contentful/blog/types";
import { getAllContentfulPaths } from "@contentful/shared";
import { useApplication } from "@ryerson/frontend.application";
import { LanguageContent } from "@ryerson/frontend.common";

/**
 * Whether `slugName` is a `blogCategory` slug, by fetching every category slug from Contentful
 * (`src/contentful/shared/index.tsx` → `getAllContentfulPaths`). No caller in `src/`:
 * `[slug].tsx` makes the same check with its own `getCategorySlugs`.
 */
export async function isCategoryPage(slugName: string) {
	let pathSlugs = await getAllContentfulPaths("blogCategory");
	let allSlugStrings = pathSlugs.find((slugString) => slugString === slugName);
	return !!allSlugStrings;
}

interface BlogCategoryMetaHeadProps {
	translatedCategory: TranslatedBlogCategorySlugs;
}

/** The category page's `<head>` tags; see the file header. */
const BlogCategoryMetaHead: React.FC<BlogCategoryMetaHeadProps> = ({ translatedCategory }) => {
	const {
		localization: { language },
	} = useApplication();

	const AllKeys = Object.keys(translatedCategory);

	let TranslatedMetaObject = AllKeys.reduce((acc, langKey) => {
		let translatedCatKey = langKey as keyof TranslatedBlogCategorySlugs;
		acc[translatedCatKey] = {
			title:
				translatedCategory[translatedCatKey].seoTitle ||
				translatedCategory[translatedCatKey].title,
			description:
				translatedCategory[translatedCatKey].seoDescription ||
				translatedCategory[translatedCatKey].blurb1.blurb1,
			url:
				"/metal-resources/metal-market-intelligence/" +
				translatedCategory[translatedCatKey].slug,
			imageUrl: translatedCategory[translatedCatKey].heroImage.file.url,
			company: false,
		};
		return acc;
	}, {} as LanguageContent<MetaContent>);

	return <MetaHead content={TranslatedMetaObject[language]} />;
};

export default BlogCategoryMetaHead;
